// ============================================
// Path: /frontend/src/pages/admin/ContactsPage.jsx
// Purpose: Admin page to view and manage contact form submissions
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { contactsAPI } from '../../api/contacts.api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Skeleton } from '../../components/ui/skeleton';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <CardContent className="space-y-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-16 w-full" />
    ))}
  </CardContent>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function ContactsPage() {
  console.log('🎨 [ContactsPage] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchContacts = async () => {
      console.log('🔵 [ContactsPage] Fetching contacts...');
      setLoading(true);

      try {
        const data = await contactsAPI.getAllContacts();
        console.log('✅ [ContactsPage] Contacts received:', data);
        setContacts(data.contacts || []);
        setError(null);
      } catch (err) {
        console.error('❌ [ContactsPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load contact messages');
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // ========================================
  // RENDER
  // ========================================
  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact Messages</CardTitle>
        <CardDescription>
          Review messages submitted through the website's contact form.
        </CardDescription>
      </CardHeader>

      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <CardContent>
          <p className="text-destructive">Error loading messages: {error}</p>
        </CardContent>
      ) : (
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {contacts?.map((contact) => (
              <AccordionItem value={contact.id} key={contact.id}>
                <AccordionTrigger>
                  <div className="flex justify-between items-center w-full pr-4">
                    <div className="text-left">
                      <p className="font-semibold">{contact.subject}</p>
                      <p className="text-sm text-muted-foreground">
                        {contact.name} ({contact.email})
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {contact.createdAt ? format(new Date(contact.createdAt), 'PPP') : 'N/A'}
                    </p>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="whitespace-pre-wrap text-muted-foreground">
                  {contact.message}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {contacts?.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No contact messages found.</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}